import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { uniq, toLower, uniqBy } from 'lodash';
import classnames from 'classnames';
import { fmtMoney } from '@upstart/harmony/lib/formatter';
import { isEmbeddedInCommandCenter } from 'util/commandCenter';
import { VehicleSelectedAction } from 'util/amplitudeEvents';
import carsActions from '../../redux/cars/actions';
import Pagination from '../../components/Pagination';
import VINSearch from '../../components/VINSearch';
import CloseIcon from '../../components/Icons/Close';
import CarImage from '../../components/CarImage';
import AddVehicle from '../../components/AddVehicle';

const PAGE_SIZE = 20;

// Generate filter options
const generateFilteredData = ({ data, filter }) => {
  let filteredList = data || [];
  const makeOptions = uniq(filteredList.map(c => c.make));
  let modelOptions = [];
  let trimOptions = [];

  if (filter.make) {
    filteredList = filteredList.filter(c => c.make === filter.make);
    modelOptions = uniq(filteredList.map(c => c.model));
  }

  if (filter.make && filter.model) {
    filteredList = filteredList.filter(c => c.model === filter.model);
    trimOptions = uniq(filteredList.map(c => c.trim));
  }

  if (filter.trim) {
    filteredList = filteredList.filter(c => c.trim === filter.trim);
  }

  makeOptions.sort();
  modelOptions.sort();
  trimOptions.sort();

  return { filteredList, makeOptions, modelOptions, trimOptions };
};

const getUniqData = data => uniqBy(uniqBy(data, 'id'), 'stock');

function VehicleSelection(props) {
  const {
    actions,
    data,
    dealer,
    fetching,
    sendAddVehicleAmplitudeEvent,
  } = props;
  const [showVINSearch, setShowVINSearch] = useState(false);
  const [carList, setCarList] = useState([]);
  const [filter, setFilter] = useState({});
  const [stock, setStock] = useState('');
  const [page, setPage] = useState(1);

  const dealerId = (dealer || {}).id;

  useEffect(() => {
    // Load car list on start
    if (dealerId) {
      actions.carListRequest({ dealer: dealerId });
    }
  }, [actions, dealerId]);

  useEffect(() => {
    setCarList(getUniqData(data));
  }, [data]);

  const offset = (page - 1) * PAGE_SIZE;
  const currPageList = carList.slice(offset, offset + PAGE_SIZE);

  const { makeOptions, modelOptions, trimOptions } = generateFilteredData({
    data,
    filter,
  });

  const searchByStock = e => {
    e.preventDefault();
    let newCarList = getUniqData(data);

    if (sendAddVehicleAmplitudeEvent) {
      sendAddVehicleAmplitudeEvent(VehicleSelectedAction.SEARCH_STOCK_START);
    }

    if (stock) {
      newCarList = newCarList.filter(c => toLower(c.stock) === toLower(stock));
    }

    // Reset data on stock search
    setPage(1);
    setFilter({});
    setCarList(newCarList);
  };

  const AddDecodedCar = React.useCallback(
    decodedCar => (
      <AddVehicle
        car={decodedCar}
        onClose={() => {
          if (sendAddVehicleAmplitudeEvent) {
            sendAddVehicleAmplitudeEvent(
              VehicleSelectedAction.ADDOTHER_VIN_CANCEL,
            );
          }
          setShowVINSearch(false);
        }}
        onSubmit={() => {
          if (sendAddVehicleAmplitudeEvent) {
            sendAddVehicleAmplitudeEvent(
              VehicleSelectedAction.ADDOTHER_VIN_FINISH,
            );
          }
          props.onSelect(decodedCar);
        }}
        dealer={dealer}
      />
    ),
    [setShowVINSearch, props.onSelect, dealer],
  );

  return (
    <div className="vehicle-selection-container">
      {props.onClose && <CloseIcon onClick={props.onClose} />}
      {showVINSearch && (
        <VINSearch
          dealer={dealer}
          allowManualEntry
          onClose={() => setShowVINSearch(false)}
          onSubmit={props.onSelect}
          onManuallyCreatedCar={props.onSelect}
          decodedCarCallback={AddDecodedCar}
          buttonText="Search"
          sendAddVehicleAmplitudeEvent={sendAddVehicleAmplitudeEvent}
        />
      )}
      <div className="vehicle-selection-row">
        <div className="vehicle-selection-input-label">SEARCH BY STOCK</div>
        <input
          type="text"
          name="stock"
          className="vehicle-selection-input"
          value={stock}
          onChange={e => setStock(e.target.value)}
        />
        <button
          className={classnames({
            btn: true,
            'vehicle-selection-button': true,
            'btn-primary': !isEmbeddedInCommandCenter(),
            'cc-button-secondary': true,
          })}
          onClick={searchByStock}
        >
          Search
        </button>
        <div className="vehicle-selection-label">OR</div>
        <div className="vehicle-selection-input-label">MAKE</div>
        <div className="vehicle-selection-select-container">
          <select
            value={filter.make || ''}
            disabled={makeOptions.length === 0}
            className="form-control vehicle-selection-select"
            onChange={e => setFilter({ make: e.target.value })}
          >
            <option value="" />
            {makeOptions.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div className="vehicle-selection-input-label">MODEL</div>
        <div className="vehicle-selection-select-container">
          <select
            value={filter.model || ''}
            disabled={modelOptions.length === 0}
            className="form-control vehicle-selection-select"
            onChange={e =>
              setFilter({ make: filter.make, model: e.target.value })
            }
          >
            <option value="" />
            {modelOptions.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div className="vehicle-selection-input-label">TRIM</div>
        <div className="vehicle-selection-select-container">
          <select
            value={filter.trim || ''}
            disabled={trimOptions.length === 0}
            className="form-control vehicle-selection-select"
            onChange={e => setFilter({ ...filter, trim: e.target.value })}
          >
            <option value="" />
            {trimOptions.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <button
          className={classnames({
            btn: true,
            'vehicle-selection-button': true,
            'btn-primary': !isEmbeddedInCommandCenter(),
            'cc-button-secondary': true,
          })}
          onClick={e => {
            e.preventDefault();

            if (sendAddVehicleAmplitudeEvent) {
              sendAddVehicleAmplitudeEvent(
                VehicleSelectedAction.SEARCH_MMT_START,
              );
            }
            const { filteredList } = generateFilteredData({ data, filter });
            // Reset data on filter search
            setPage(1);
            setStock('');
            setCarList(filteredList);
          }}
        >
          Search
        </button>
      </div>
      <div className="vehicle-selection-row">
        <div
          className="vehicle-selection-new-button"
          onClick={() => {
            if (sendAddVehicleAmplitudeEvent) {
              sendAddVehicleAmplitudeEvent(
                VehicleSelectedAction.ADDOTHER_CLICKED,
              );
            }
            setShowVINSearch(true);
            if (sendAddVehicleAmplitudeEvent) {
              sendAddVehicleAmplitudeEvent(
                VehicleSelectedAction.ADDOTHER_START,
              );
            }
          }}
        >
          Add A New Vehicle
        </div>
      </div>
      {(!dealerId || fetching) && (
        <div className="vehicle-selection-loader">Loading...</div>
      )}
      {currPageList.length === 0 && dealerId && !fetching ? (
        <div className="vehicle-selection-data-label">No cars found</div>
      ) : (
        <div>
          <div className="vehicle-selection-car-list">
            {currPageList.map(car => (
              <div
                key={car.id}
                onClick={() => {
                  if (sendAddVehicleAmplitudeEvent) {
                    sendAddVehicleAmplitudeEvent(
                      VehicleSelectedAction.VEHICLE_SELECTED,
                    );
                  }
                  props.onSelect(car);
                }}
                className="vehicle-selection-car-container"
              >
                <CarImage
                  car={car}
                  className="vehicle-selection-car-image"
                  dealer={dealer}
                />
                <div className="vehicle-selection-car-data-container">
                  <div className="vehicle-selection-car-name">{car.name}</div>
                  <div className="vehicle-selection-car-data-row">
                    Stock: {car.stock}
                  </div>
                  <div className="vehicle-selection-car-data-row">
                    MSRP: {car.msrp ? fmtMoney(car.msrp) : 'N/A'}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-right">
            <Pagination
              page={page}
              perPage={PAGE_SIZE}
              total={carList.length}
              setPage={newPage => {
                // Scroll to top on page change
                document
                  .querySelector('.pencil-editor-container')
                  .scrollTo(0, 0);
                setPage(newPage);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

VehicleSelection.propTypes = {
  actions: PropTypes.object,
  dealer: PropTypes.object,
  fetching: PropTypes.bool,
  onSelect: PropTypes.func,
  onClose: PropTypes.func,
  data: PropTypes.array,
};

const mapStateToProps = ({ cars, deals, user }) => ({
  fetching: cars.carList.fetching,
  data: cars.carList.data,
  dealer: deals.dealer || user.currentUserDealer,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(carsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(VehicleSelection);
